import { useState, useCallback } from 'react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Lock, Loader2, KeyRound } from 'lucide-react';

/**
 * Inline PIN setup/change as a dialog so it can be triggered in one tap
 * (e.g. from the dashboard "Set Up PIN" banner) without navigating to Settings.
 */
export const MobilePinSetupDialog = ({ open, onOpenChange }) => {
  const { setupPin, deviceToken } = useMobileAuth();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setPin(''); setError(''); setLoading(false); };

  const handleInput = useCallback((digit) => {
    setPin((prev) => (prev.length < 6 ? prev + digit : prev));
    setError('');
  }, []);

  const handleSubmit = async () => {
    if (pin.length < 4) { setError('PIN must be at least 4 digits'); return; }
    setLoading(true);
    setError('');
    const result = await setupPin(pin);
    setLoading(false);
    if (result.success) {
      toast.success('PIN set up successfully!');
      reset();
      onOpenChange(false);
    } else {
      setError(result.error || 'Failed to set up PIN');
      setPin('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-xs" data-testid="pin-setup-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--m-text)] flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#ffd108]" />
            {deviceToken ? 'Change PIN' : 'Set Up PIN'}
          </DialogTitle>
          <DialogDescription className="text-[var(--m-text-secondary)] text-xs">
            Create a PIN for faster sign-in on this device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[var(--m-text-secondary)] text-xs uppercase flex items-center gap-1.5">
              <Lock className="h-3 w-3" />
              {deviceToken ? 'Enter New PIN' : 'Create PIN'}
            </Label>
            <div className="flex justify-center gap-2 py-3 bg-[var(--m-bg)] rounded-md border border-[var(--m-border-light)]">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={`dot-${i}`}
                  className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? 'bg-[#ffd108]' : 'bg-[var(--m-border-light)]'}`}
                  data-testid={`dialog-pin-dot-${i}`}
                />
              ))}
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center bg-red-400/10 py-2 rounded-md" data-testid="dialog-pin-error">
              {error}
            </p>
          )}

          <div className="grid grid-cols-3 gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
              <Button
                key={digit}
                variant="outline"
                className="h-12 text-lg font-bold bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
                onClick={() => handleInput(String(digit))}
                disabled={loading}
                data-testid={`dialog-pin-digit-${digit}`}
              >
                {digit}
              </Button>
            ))}
            <Button
              variant="outline"
              className="h-12 text-xs bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
              onClick={() => { setPin(''); setError(''); }}
              disabled={loading}
              data-testid="dialog-pin-clear"
            >
              Clear
            </Button>
            <Button
              variant="outline"
              className="h-12 text-lg font-bold bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
              onClick={() => handleInput('0')}
              disabled={loading}
              data-testid="dialog-pin-digit-0"
            >
              0
            </Button>
            <Button
              variant="outline"
              className="h-12 text-lg bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
              onClick={() => setPin((prev) => prev.slice(0, -1))}
              disabled={loading}
              data-testid="dialog-pin-backspace"
            >
              ←
            </Button>
          </div>

          <Button
            onClick={handleSubmit}
            className="w-full btn-volt"
            disabled={loading || pin.length < 4}
            data-testid="dialog-confirm-pin-setup"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (deviceToken ? 'Update PIN' : 'Set PIN')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MobilePinSetupDialog;
